import { memo, useEffect, useMemo, useState } from "react";

import ZipImage from "@hl/base-components/lib/ZipImage";
import {
  ChevronDown,
  ChevronUp,
} from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/Arrows";
import CustomPaginationWrapper from "@hl/base-components/lib/table/CustomPaginationWrapper";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { createStyles, Image, ScrollArea, Text } from "@mantine/core";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";

import { SeriesToken } from "~/features/collections/forms/CreateCollectionMultistepForm/context";

export const useStyles = createStyles(
  (theme, { scroll }: { scroll: boolean }) => {
    const border = `1px solid ${theme.colors.divider[0]}`;
    const radius = theme.radius.sm;
    return {
      table: {
        width: "100%",
        borderSpacing: 0,

        "tr:first-child td, tr:first-child th": {
          borderTop: border,
        },
        "tr:last-child td": {
          borderBottom: border,
        },
        "th:first-child, td:first-child": {
          borderLeft: border,
        },
        "th:last-child, td:last-child": {
          borderRight: border,
        },

        "th:first-child": {
          borderTopLeftRadius: radius,
        },

        "th:last-child": {
          borderTopRightRadius: radius,
        },

        "tr:last-child td:first-child": {
          borderBottomLeftRadius: radius,
        },

        "tr:last-child td:last-child": {
          borderBottomRightRadius: radius,
        },
      },
      sortable: {
        cursor: "pointer",
        userSelect: "none",
      },
      th: {
        paddingTop: "16px",
        paddingBottom: "16px",
        paddingLeft: "16px",
        textAlign: "left",
        minWidth: 120,
        maxWidth: 240,
        backgroundColor: theme.colors.baseBackground[0],
        "&:first-of-type": {
          paddingLeft: "16px",
        },
      },
      td: {
        minWidth: 120,
        maxWidth: 240,
        padding: "18px 0",
        backgroundColor: "inherit",
        paddingLeft: "16px",
        border: "inherit",
      },
      tr: {
        backgroundColor: theme.colors.primaryBackground[0],
        "&:nth-child(even)": {
          backgroundColor: theme.colors.tableRowBackground[0],
        },
        borderBottom: `1px solid ${theme.colors.divider[0]}`,
      },
      stickyTd: {
        position: "sticky",
        left: 0,
        ...(scroll && {
          boxShadow: theme.shadows.sm,
        }),
      },
    };
  }
);
export interface SeriesMetadataTablePreviewProps {
  tokens: SeriesToken[];
  onUpdate?: boolean;
}

const columnHelper = createColumnHelper<SeriesToken>();

const SeriesMetadataTablePreview = ({
  tokens,
  onUpdate,
}: SeriesMetadataTablePreviewProps) => {
  const [tableScrolling, setTableScrolling] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);

  const { classes, cx } = useStyles({ scroll: tableScrolling });

  const [currentTokens, setCurrentTokens] = useState<SeriesToken[]>([]);
  const [activePage, setPage] = useState(1);
  const [lowerBound, setLowerBound] = useState(1);
  const [upperBound, setUpperBound] = useState(onUpdate ? 10 : 1);

  const getAllKeys = (): string[] => {
    const keys: { [key: string]: boolean } = {};
    for (const currentToken of currentTokens) {
      for (const key of Object.keys(currentToken)) {
        keys[key] = true;
      }
    }
    return Object.keys(keys);
  };

  const columns = useMemo(() => {
    const columns = [];

    if (currentTokens.length) {
      if (onUpdate) {
        columns.push(
          columnHelper.accessor("metadataId", {
            id: "metadataId",
            header: "metadataId",
            cell: (info) => {
              const data = info.getValue();
              return data;
            },
          })
        );
      }

      if (getAllKeys().includes("imageEntry")) {
        columns.push(
          columnHelper.accessor("imageEntry", {
            header: () => "Artwork",
            cell: (info) => {
              const image = info.getValue();
              return image ? (
                <ZipImage image={image} maxWidth={72} maxHeight={72} />
              ) : undefined;
            },
          })
        );
      }
      if (getAllKeys().includes("imageUrl")) {
        columns.push(
          columnHelper.accessor("imageUrl", {
            header: () => "Artwork",
            cell: (info) => {
              const image = info.getValue();
              return image ? (
                <Image src={image} maw={72} mah={72} fit="contain" />
              ) : undefined;
            },
          })
        );
      }

      if (getAllKeys().includes("animationEntry")) {
        columns.push(
          columnHelper.accessor("animationEntry", {
            header: () => "Animation",
            cell: (info) => {
              const animation = info.getValue();
              return animation ? (
                <ZipImage
                  image={animation}
                  maxWidth={72}
                  maxHeight={72}
                  isAnimation={true}
                />
              ) : undefined;
            },
          })
        );
      }

      columns.push(
        ...getAllKeys()
          .filter(
            (tokenAttr) =>
              ![
                "imageUrl",
                "imageEntry",
                "animationUrl",
                "animationEntry",
                "metadataId",
              ]
                .concat(onUpdate ? [] : ["imageRef", "animationRef"])
                .includes(tokenAttr)
          )
          .map((key) =>
            columnHelper.accessor(key, {
              id: key,
              header: key,
              cell: (info) => {
                const data = info.getValue();
                if (typeof data === "object") {
                  return "Invalid attribute";
                }

                return "" + data;
              },
            })
          )
      );
    }
    return columns;
  }, [currentTokens]);

  const table = useReactTable({
    data: currentTokens,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    setCurrentTokens(tokens.slice(lowerBound - 1, upperBound));
  }, [lowerBound, upperBound, tokens]);

  return (
    <CustomPaginationWrapper
      activePage={activePage}
      setPage={setPage}
      setLowerBound={setLowerBound}
      setUpperBound={setUpperBound}
      maxLength={tokens.length}
      itemsPerPage={10}
      border={false}
      padding="0"
    >
      <ScrollArea
        style={{ maxHeight: 1200 }}
        offsetScrollbars
        onScrollPositionChange={({ x }) => {
          if (x > 1 && !tableScrolling) {
            setTableScrolling(true);
          } else if (x <= 1 && tableScrolling) {
            setTableScrolling(false);
          }
        }}
      >
        <table className={classes.table}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, idx) => (
                  <th
                    key={header.id}
                    className={cx(
                      classes.th,
                      header.column.getCanSort() ? classes.sortable : null,
                      {
                        [classes.stickyTd]: idx === 0,
                      }
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <Text
                      size="sm"
                      weight={500}
                      sx={{
                        gap: 4,
                        display: "flex",
                        alignItems: "center",
                      }}
                      color={
                        header.column.getIsSorted()
                          ? TEXT_COLOR.PRIMARY
                          : TEXT_COLOR.PLACEHOLDER
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      {{
                        asc: <ChevronUp width={16} height={16} />,
                        desc: <ChevronDown width={16} height={16} />,
                      }[header.column.getIsSorted() as string] ?? null}
                    </Text>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className={classes.tr}>
                {row.getVisibleCells().map((cell, idx) => (
                  <td
                    key={cell.id}
                    className={cx(classes.td, {
                      [classes.stickyTd]: idx === 0,
                    })}
                  >
                    <Text size="sm">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Text>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollArea>
    </CustomPaginationWrapper>
  );
};

export default memo(SeriesMetadataTablePreview);
